/* it7 ⇄ Swym Wishlist Plus bridge (2026-06-12)
   Keeps the it7 wishlist UI (hearts, PDP button + View Wishlist slide-in, header
   count) exactly as designed, but makes Swym the durable backend:
   - On load, Swym's list is fetched and mirrored into the `it7wish` localStorage
     key that all existing inline painters already read → instant, consistent UI.
   - Clicks keep their optimistic localStorage toggle (zero-latency animation);
     this adapter observes the click one tick later and syncs the add/remove to
     Swym via their v3 JS API (epi = variant id, empi = product id, du = URL,
     carried as data attributes on the buttons).
   Swym's own injected buttons/launcher must be disabled in the Swym admin.
   NOTE (2026-06-12): on this store Swym initializes unreliably — the
   window.SwymCallbacks queue is never drained (their relay extension errors
   during init) and in some sessions window._swat never appears at all. So:
   - we poll for window._swat as well as queueing the callback, and
   - every wishlist click ALSO records {empi → du, epi} in the `it7wishd`
     localStorage map, independent of Swym, so the wishlist page can render
     saved items even when Swym is completely dead. */
(function () {
  var KEY = 'it7wish';
  var DKEY = 'it7wishd';
  var PKEY = 'it7wishp';   /* ITA2-1186: empi → base-currency cents at save time (price-drop baseline) */
  function lsGet() { try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (e) { return []; } }
  function lsSet(a) { try { localStorage.setItem(KEY, JSON.stringify(a)); } catch (e) {} }
  function dGet() { try { return JSON.parse(localStorage.getItem(DKEY) || '{}'); } catch (e) { return {}; } }
  function dSet(o) { try { localStorage.setItem(DKEY, JSON.stringify(o)); } catch (e) {} }
  function pGet() { try { return JSON.parse(localStorage.getItem(PKEY) || '{}'); } catch (e) { return {}; } }
  function pSet(o) { try { localStorage.setItem(PKEY, JSON.stringify(o)); } catch (e) {} }

  /* ── ITA2-1186 price-drop detection ─────────────────────────────────────────
     Baseline: the product's base price (cents, primary-market currency from
     /products/{h}.js) recorded when the item is ADDED. Both sides of the compare
     come from the same endpoint, so the percentage is currency-invariant and safe
     to show under any Markets presentment. Items saved before this shipped have no
     baseline — their first check records one silently (no false alerts, drops
     tracked from then on). The sweep runs at most once per session (sessionStorage
     guard), capped, and its result is cached for the header bell + wishlist page. */
  var SKEY = 'it7wishdrops';
  function basePrice(p) {
    var v = p && p.variants && p.variants[0];
    return v ? v.price : 0;
  }
  /* /products/{h}.js prices follow the ACTIVE market's currency — a baseline saved under
     KRW compared against USD reads as a ~100% "drop" (found by Jamil, 2026-07-30). So a
     baseline is {p, c}: price cents + currency, and it only ever compares against a price
     in the SAME currency; a market switch resets the baseline silently instead of lying. */
  function curNow() { try { return (window.Shopify && Shopify.currency && Shopify.currency.active) || ''; } catch (e) { return ''; } }
  function recordBaseline(empi, du) {
    var handle = (du || '').split('?')[0].split('/products/')[1];
    if (!handle) return;
    fetch('/products/' + handle + '.js').then(function (r) { return r.ok ? r.json() : null; }).then(function (p) {
      var cents = basePrice(p);
      if (!cents) return;
      var m = pGet();
      if (!m[empi]) { m[empi] = { p: cents, c: curNow() }; pSet(m); }
    }).catch(function () {});
  }
  function computeDrops(force) {
    var cached = null;
    try { cached = JSON.parse(sessionStorage.getItem(SKEY) || 'null'); } catch (e) {}
    if (cached && !force) { paintBell(cached); return; }
    var ids = lsGet();
    if (!ids.length) { try { sessionStorage.setItem(SKEY, JSON.stringify({ n: 0, drops: {} })); } catch (e) {} paintBell({ n: 0 }); return; }
    var d = dGet(), m = pGet(), drops = {}, pending = 0, capped = ids.slice(0, 30);
    function done() {
      var out = { n: Object.keys(drops).length, drops: drops };
      try { sessionStorage.setItem(SKEY, JSON.stringify(out)); } catch (e) {}
      pSet(m);
      paintBell(out);
      try { document.dispatchEvent(new CustomEvent('it7:wishdrops', { detail: out })); } catch (e) {}
    }
    capped.forEach(function (id) {
      var du = d[id] && d[id].du;
      var handle = (du || '').split('?')[0].split('/products/')[1];
      if (!handle) return;
      pending++;
      fetch('/products/' + handle + '.js').then(function (r) { return r.ok ? r.json() : null; }).then(function (p) {
        var now = basePrice(p);
        if (now) {
          var cur = curNow();
          var b = m[id];
          if (typeof b === 'number') b = null;           /* pre-fix numeric baseline: currency unknown — reset */
          if (!b || b.c !== cur) m[id] = { p: now, c: cur };  /* new item / market switch: adopt, never compare */
          else if (now < b.p) {
            var pct = Math.round((b.p - now) * 100 / b.p);
            if (pct >= 1) drops[id] = { s: b.p, now: now, pct: pct };
          } else if (now > b.p) m[id] = { p: now, c: cur };   /* price went UP: reset so a later cut is a real drop */
        }
      }).catch(function () {}).then(function () { if (--pending === 0) done(); });
    });
    if (!pending) done();
  }
  /* bell on the header WISHLIST link when any saved item dropped */
  function paintBell(state) {
    /* some header variants render the wishlist link without the id — fall back to href */
    var wl = document.getElementById('it7-wishlink') || document.querySelector('a[href*="swym-wishlist"]');
    if (!wl) return;
    var b = wl.querySelector('.it7-wl-bell');
    if (state && state.n > 0) {
      if (!b) {
        b = document.createElement('span');
        b.className = 'it7-wl-bell';
        b.setAttribute('aria-label', 'Price drop in your wishlist');
        b.innerHTML = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 2a3.6 3.6 0 0 0-3.6 3.6c0 3-1.4 4.4-1.4 4.4h10s-1.4-1.4-1.4-4.4A3.6 3.6 0 0 0 8 2z"/><path d="M6.8 12.6a1.3 1.3 0 0 0 2.4 0"/></svg>';
        wl.appendChild(b);
      }
    } else if (b) b.remove();
  }

  function paint() {
    var w = lsGet();
    document.querySelectorAll('.hrt[data-it7-wish]').forEach(function (b) {
      var on = w.indexOf(b.dataset.it7Wish) > -1;
      b.classList.toggle('on', on);
      b.textContent = on ? '♥' : '♡';
    });
    var wb = document.getElementById('it7-wshbtn');
    if (wb) {
      var on = w.indexOf(wb.dataset.it7Wish) > -1;
      wb.classList.toggle('on', on);
      wb.innerHTML = on ? '♥' : '♡';
      wb.setAttribute('aria-pressed', on ? 'true' : 'false');
    }
    var wl = document.getElementById('it7-wishlink');
    if (wl) { var n = w.length; wl.textContent = n > 0 ? 'Wishlist (' + n + ')' : 'Wishlist'; }
  }

  /* Swym-independent: keep the empi→{du,epi} detail map in step with every
     wishlist click so the wishlist page can render without Swym. */
  document.addEventListener('click', function (e) {
    var el = e.target.closest('[data-it7-wish]');
    if (!el) return;
    setTimeout(function () {
      var id = el.dataset.it7Wish;
      if (!id) return;
      var d = dGet();
      if (lsGet().indexOf(id) > -1) {
        d[id] = { du: el.dataset.it7Du || (location.origin + location.pathname), epi: el.dataset.it7Epi || '' };
        markAdd(id, d[id]);
        recordBaseline(id, d[id].du);   /* ITA2-1186: price at save time = drop baseline */
        /* ITA2-933: wishlist adds previously emitted NO analytics event. Fire a
           Klaviyo "Added to Wishlist" event on the ADD toggle only (not remove),
           using the identifiers already on the button. Guarded so it never throws
           if the Klaviyo SDK hasn't loaded — analytics must not break the UX. */
        try {
          if (window._learnq) {
            window._learnq.push(['track', 'Added to Wishlist', {
              ProductID: id,
              VariantID: el.dataset.it7Epi || undefined,
              URL: d[id].du
            }]);
          }
        } catch (err) { /* never break the wishlist toggle on analytics failure */ }
      } else {
        delete d[id];
        var pm = pGet(); delete pm[id]; pSet(pm);   /* ITA2-1186: baseline dies with the item */
        markRemove(id);
      }
      dSet(d);
    }, 0);
  });

  /* ITA2-1186: one drop-sweep per session, deferred so it never competes with paint */
  if (window.requestIdleCallback) requestIdleCallback(function () { computeDrops(false); }, { timeout: 8000 });
  else setTimeout(function () { computeDrops(false); }, 4000);
  window.it7WishDrops = computeDrops;   /* wishlist page forces a fresh sweep */

  var swat = null;
  var swymList = [];

  /* Swym's fetch() serves a cached list that lags its own add/remove calls (Codex trace,
     2026-09-30: add returned 200, fetch() still [] seconds later). So local intent is kept
     until a fetched list SHOWS it: `it7wishpend` = adds not yet seen in Swym's list,
     `it7wishrm` = removals Swym's list still shows. A 400 "Unknown product" add simply
     stays pending (shown locally, retried on the next page) instead of being lost. */
  var PENDKEY = 'it7wishpend', RMKEY = 'it7wishrm';
  function jGet(k) { try { return JSON.parse(localStorage.getItem(k) || '{}'); } catch (e) { return {}; } }
  function jSet(k, o) { try { localStorage.setItem(k, JSON.stringify(o)); } catch (e) {} }
  function pendGet() { return jGet(PENDKEY); }
  function pendSet(o) { jSet(PENDKEY, o); }
  function markAdd(id, det) { var p = pendGet(); p[id] = { du: det.du, epi: String(det.epi || '') }; pendSet(p); var r = jGet(RMKEY); if (r[id]) { delete r[id]; jSet(RMKEY, r); } }
  function markRemove(id) { var p = pendGet(); if (p[id]) { delete p[id]; pendSet(p); } var r = jGet(RMKEY); r[id] = 1; jSet(RMKEY, r); }
  /* Fetched ids + unconfirmed adds − unconfirmed removals; clears whatever the fetch confirms. */
  function reconcile(fetchedIds) {
    var p = pendGet(), r = jGet(RMKEY), has = {};
    fetchedIds.forEach(function (id) { has[id] = 1; });
    Object.keys(p).forEach(function (id) { if (has[id]) delete p[id]; });
    Object.keys(r).forEach(function (id) { if (!has[id]) delete r[id]; });
    pendSet(p); jSet(RMKEY, r);
    return fetchedIds.filter(function (id) { return !r[id]; }).concat(Object.keys(p));
  }
  window.it7WishReconcile = reconcile;
  window.it7WishRemove = markRemove;
  function swymAdd(empi, epi, du) {
    try {
      swat.addToWishList({ epi: parseInt(epi, 10) || parseInt(empi, 10), empi: parseInt(empi, 10), du: du }, function () {
        syncFromSwym();
        try { document.dispatchEvent(new CustomEvent('it7:wish-synced')); } catch (e) {}
      });
    } catch (err) {}
  }
  /* Programmatic add (Save for later → Move to wishlist): same storage, baseline, header
     count and Swym sync as a heart click; pending until a Swym fetch shows it. */
  window.it7WishAdd = function (empi, epi, du) {
    empi = String(empi);
    var w = lsGet();
    if (w.indexOf(empi) < 0) { w.push(empi); lsSet(w); }
    var d = dGet(); d[empi] = { du: du, epi: String(epi || '') }; dSet(d);
    recordBaseline(empi, du);
    paint();
    markAdd(empi, { du: du, epi: epi });
    if (swat) swymAdd(empi, epi, du);
  };

  function syncFromSwym() {
    if (!swat) return;
    try {
      swat.fetch(function (list) {
        swymList = list || [];
        lsSet(reconcile(swymList.map(function (i) { return String(i.empi); })));
        var d = dGet();
        swymList.forEach(function (i) { if (i.du) d[String(i.empi)] = { du: i.du, epi: String(i.epi || (d[String(i.empi)] || {}).epi || '') }; });
        dSet(d);
        paint();
      });
    } catch (e) { /* Swym unavailable — UI keeps working on localStorage alone */ }
  }

  function init(_swat) {
    if (swat || !_swat) return; /* bind once, whichever path fires first */
    swat = _swat;
    var pend = pendGet();
    Object.keys(pend).forEach(function (id) { swymAdd(id, pend[id].epi, pend[id].du); });
    syncFromSwym();

    /* Observe wishlist clicks one tick after the inline optimistic handlers ran,
       then push the resulting state to Swym (idempotent on their side). */
    document.addEventListener('click', function (e) {
      var el = e.target.closest('[data-it7-wish]');
      if (!el || !swat) return;
      setTimeout(function () {
        var id = el.dataset.it7Wish;
        var empi = parseInt(id, 10);
        if (!empi) return;
        var on = lsGet().indexOf(id) > -1;
        var epi = parseInt(el.dataset.it7Epi || '', 10) || empi;
        var du = el.dataset.it7Du || (location.origin + location.pathname);
        if (on) {
          try { swat.addToWishList({ epi: epi, empi: empi, du: du }, function () { syncFromSwym(); }); } catch (err) {}
        } else {
          var item = swymList.filter(function (i) { return String(i.empi) === id; })[0] || { epi: epi, empi: empi, du: du };
          try { swat.removeFromWishList(item, function () { syncFromSwym(); }); } catch (err) {}
        }
      }, 0);
    });
  }

  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push(init);

  /* Fallback: poll for window._swat for up to ~60s; the callbacks queue is not
     drained on this store, so this is the path that actually binds in practice. */
  var tries = 0;
  var poll = setInterval(function () {
    if (swat) { clearInterval(poll); return; }
    if (window._swat) { clearInterval(poll); init(window._swat); }
    else if (++tries > 200) { clearInterval(poll); }
  }, 300);
})();
